// src/viqai/ViqCards.jsx
// ─── Cartes de résultat VIQ.AI (§13) ────────────────────────────────────────
// Une réponse de VIQ.AI n'est pas du Markdown : chaque outil renvoie un type de
// carte déclaré dans `ai/schemas/cards.js`, rendu ici. Un type inconnu retombe
// sur la carte de repli — l'agent peut évoluer sans casser l'affichage.
//
// v222 — la Cote VIQ.AI® est rendue par une carte dédiée et provient
// exclusivement du moteur de valorisation unifié côté serveur.

import { useState, useEffect } from "react";
import { buildLeBonCoinUrl, apiGet } from "./api";
import { downloadResult, downloadMediaZip } from "./media-download";

const eur = (n) => (n == null || isNaN(Number(n)) ? "—" : `${Number(n).toLocaleString("fr-FR")} €`);
const km = (n) => (n == null ? "—" : `${Number(n).toLocaleString("fr-FR")} km`);

function Card({ title, sub, children, tone }) {
  return (
    <div className={`viq-card${tone ? ` ${tone}` : ""}`}>
      {(title || sub) && (
        <div className="viq-card-head">
          {title && <div className="viq-card-title">{title}</div>}
          {sub && <div className="viq-card-sub">{sub}</div>}
        </div>
      )}
      {children}
    </div>
  );
}

function Row({ k, v, tone, total }) {
  return (
    <div className={`viq-row${total ? " total" : ""}`}>
      <span className="k">{k}</span>
      <span className={`v${tone ? ` ${tone}` : ""}`}>{v}</span>
    </div>
  );
}

function Actions({ items }) {
  const list = (items || []).filter(Boolean);
  if (!list.length) return null;
  return (
    <div className="viq-actions">
      {list.map((a, i) => (
        <button key={i} type="button" className={`viq-btn${a.primary ? " primary" : ""}`} onClick={a.onClick}>
          {a.label}
        </button>
      ))}
    </div>
  );
}

// ── Véhicule identifié ──────────────────────────────────────────────────────
function VehicleCard({ data, onAsk, onOpenDossier }) {
  if (data.found === false) {
    return <Card title="Véhicule non identifié" sub={data.identifier}>
      <div className="viq-empty">{data.message}</div>
    </Card>;
  }
  const label = [data.make, data.model, data.year].filter(Boolean).join(" ");
  return (
    <Card title={label} sub={data.plaque || data.vin}>
      <div className="viq-chips">
        {data.version && <span className="viq-chip">{data.version}</span>}
        {data.fuel && <span className="viq-chip">{data.fuel}</span>}
        {data.gearbox && <span className="viq-chip">{data.gearbox}</span>}
        {data.power_cv && <span className="viq-chip">{data.power_cv} CV</span>}
        {data.km != null && <span className="viq-chip">{km(data.km)}</span>}
      </div>
      <Actions items={[
        data.dossier_id && onOpenDossier
          ? { label: "Ouvrir le dossier", primary: true, onClick: () => onOpenDossier(data.dossier_id, "overview") }
          : { label: "Créer le dossier", primary: true, onClick: () => onAsk(`Crée un dossier pour ${data.plaque || data.vin}.`) },
        { label: "Analyser la marge", onClick: () => onAsk("Analyse la marge possible sur ce véhicule.") },
        { label: "Voir le marché", onClick: () => onAsk("Cherche les annonces comparables de ce véhicule.") },
      ]} />
    </Card>
  );
}

function VehicleListCard({ data, onAsk, onOpenDossier }) {
  const items = Array.isArray(data?.items) ? data.items : [];
  const total = Number(data?.count ?? items.length);
  const foundCount = Number(data?.found_count ?? items.filter(x => x?.found).length);
  return (
    <Card title={`${foundCount}/${total} véhicule${total > 1 ? "s" : ""} identifié${foundCount > 1 ? "s" : ""}`}>
      <div className="viq-vehicle-batch" style={{display:"grid",gap:".7rem"}}>
        {items.map((v,i) => {
          const label=v?.found ? [v.make,v.model,v.year].filter(Boolean).join(" ") : "Véhicule non identifié";
          const ident=v?.plaque || v?.vin || v?.identifier || `Identifiant ${i+1}`;
          return <div key={`${ident}-${i}`} className="viq-web-source" style={{border:"1px solid var(--ws-border,var(--border))",borderRadius:12,padding:".75rem .8rem",background:"var(--ws-surface,var(--card))"}}>
            <div style={{display:"flex",justifyContent:"space-between",gap:".8rem",alignItems:"flex-start",flexWrap:"wrap"}}>
              <div style={{minWidth:0}}>
                <strong>{label}</strong>
                <div className="viq-card-sub" style={{marginTop:".15rem"}}>{ident}{v?.existing && v?.dossier_id ? ` · Dossier VIQ #${v.dossier_id}` : ""}</div>
                {v?.found && <div className="viq-chips" style={{marginTop:".45rem"}}>
                  {v.version && <span className="viq-chip">{v.version}</span>}
                  {v.fuel && <span className="viq-chip">{v.fuel}</span>}
                  {v.gearbox && <span className="viq-chip">{v.gearbox}</span>}
                  {v.power_cv && <span className="viq-chip">{v.power_cv} CV</span>}
                  {v.km != null && <span className="viq-chip">{km(v.km)}</span>}
                </div>}
                {!v?.found && <div className="viq-card-sub" style={{marginTop:".35rem"}}>{v?.message || "Identification non disponible."}</div>}
              </div>
              <Actions items={v?.found ? [
                v?.dossier_id && onOpenDossier ? {label:"Ouvrir le dossier",primary:true,onClick:()=>onOpenDossier(v.dossier_id)} : {label:"Créer le dossier",primary:true,onClick:()=>onAsk(`Crée un dossier pour ${ident}.`)},
                {label:"Cote VIQ.AI®",onClick:()=>onAsk(`Donne-moi la Cote VIQ.AI® du véhicule ${ident}.`)},
              ].filter(Boolean) : []} />
            </div>
          </div>;
        })}
      </div>
    </Card>
  );
}

// ── Dossier VIQ ─────────────────────────────────────────────────────────────
function DossierCard({ data, onAsk, onOpenDossier }) {
  return (
    <Card title={data.label || `Dossier #${data.id}`} sub={data.plaque || data.vin || null}>
      <div className="viq-rows">
        {data.km != null && <Row k="Kilométrage" v={km(data.km)} />}
        {data.score != null && <Row k="Score VIQ.AI®" v={`${data.score}/100`} tone={data.score >= 75 ? "pos" : data.score >= 55 ? "warn" : "neg"} />}
        {data.next_action && <Row k="Prochaine étape" v={data.next_action} />}
        {data.created && <Row k="Statut" v="Dossier créé" tone="pos" />}
      </div>
      {data.vigilance?.length > 0 && (
        <ul className="viq-bullets">
          {data.vigilance.slice(0, 3).map((v, i) => <li key={i}>{v}</li>)}
        </ul>
      )}
      <Actions items={[
        { label: "Ouvrir le dossier", primary: true, onClick: () => onOpenDossier(data.id) },
        !data.has_photos && { label: "Analyser des photos", onClick: () => onAsk("Analyse les photos de ce véhicule.") },
        { label: "Calculer la marge", onClick: () => onAsk("Quelle marge puis-je faire sur ce véhicule ?") },
      ]} />
    </Card>
  );
}

function DossierList({ data, onOpenDossier, onAsk }) {
  if (!data.count) {
    return <Card title="Aucun dossier">
      <div className="viq-empty">Rien ne correspond{data.query ? ` à « ${data.query} »` : ""}. Donnez-moi une plaque ou un VIN et j'ouvre un dossier.</div>
    </Card>;
  }
  return (
    <Card title={`${data.count} dossier${data.count > 1 ? "s" : ""}`} sub={data.filter && data.filter !== "all" ? data.filter : null}>
      <div className="viq-rows">
        {data.dossiers.map(d => (
          <div key={d.id} className="viq-row" style={{ cursor: "pointer" }} onClick={() => onOpenDossier(d.id)}>
            <span className="k">
              {d.label || [d.make, d.model, d.year].filter(Boolean).join(" ")}
              {d.plaque ? ` · ${d.plaque}` : ""}
              {d.jours_inactif != null ? ` · ${d.jours_inactif} j` : ""}
            </span>
            <span className="v">{d.marge_potentielle != null ? eur(d.marge_potentielle) : d.km != null ? km(d.km) : "—"}</span>
          </div>
        ))}
      </div>
      {data.stats && (
        <div className="viq-chips">
          <span className="viq-chip">{data.stats.totalDossiers ?? 0} au total</span>
          {data.stats.dormants > 0 && <span className="viq-chip warn">{data.stats.dormants} dormants</span>}
        </div>
      )}
      <Actions items={[{ label: "Que faut-il relancer ?", onClick: () => onAsk("Lesquels dois-je relancer en priorité et pourquoi ?") }]} />
    </Card>
  );
}

// ── Marge ───────────────────────────────────────────────────────────────────
// La marge reste une vue opérationnelle distincte de la cote : charges, leviers et cible de revente.
function MarginCard({ data, onAsk, onOpenDossier }) {
  if (!data.available) {
    return (
      <Card title="Marge non calculable">
        <div className="viq-empty">{data.message}</div>
        {data.missing?.length > 0 && (
          <div className="viq-chips">{data.missing.map((m, i) => <span key={i} className="viq-chip warn">{m} manquant</span>)}</div>
        )}
        <Actions items={[{ label: "Compléter le dossier", primary: true, onClick: () => onOpenDossier(data.dossier_id) }]} />
      </Card>
    );
  }
  const c = data.charges || {};
  return (
    <Card title={data.label || "Marge de l'opération"} sub={data.km != null ? km(data.km) : null}>
      <div className="viq-rows">
        {data.prix_achat != null && <Row k="Prix d'achat annoncé" v={eur(data.prix_achat)} />}
        {c.preparation != null && <Row k="Préparation" v={eur(c.preparation)} />}
        {c.fonctionnement != null && <Row k="Frais de fonctionnement" v={eur(c.fonctionnement)} />}
        {c.acquisition != null && <Row k="Acquisition client" v={eur(c.acquisition)} />}
        {data.extra_costs ? <Row k="Frais ajoutés" v={eur(data.extra_costs)} /> : null}
        <Row k="Charges imputables" v={eur(data.charges_total)} />
        {data.levier_negociation_eur != null && <Row k="Levier de négociation" v={eur(data.levier_negociation_eur)} tone="pos" />}
        {data.marge_suggeree?.montant != null && <Row k="Marge brute suggérée" v={eur(data.marge_suggeree.montant)} total />}
        {data.prix_revente_cible != null && <Row k="Revente à viser" v={eur(data.prix_revente_cible)} />}
      </div>

      {data.axes_negociation?.length > 0 && (
        <>
          <div className="viq-card-sub" style={{ marginTop: ".9rem" }}>Ce que vous pouvez faire valoir</div>
          <ul className="viq-bullets">
            {data.axes_negociation.slice(0, 5).map((a, i) => (
              <li key={i}><strong>{a.titre}</strong>{a.levier_eur ? ` — ${eur(a.levier_eur)}` : ""} : {a.detail}</li>
            ))}
          </ul>
        </>
      )}
      <div className="viq-card-sub" style={{ marginTop: ".8rem" }}>{data.avertissement}</div>
      <Actions items={[
        { label: "Ouvrir le dossier", onClick: () => onOpenDossier(data.dossier_id) },
        { label: "Ajouter des frais", onClick: () => onAsk("Ajoute des frais à cette opération : ") },
      ]} />
    </Card>
  );
}

// ── Analyse photo (§13, §50) ────────────────────────────────────────────────
// Même richesse que le Dossier VIQ : identification, plaque détectée, zones,
// score visuel, points de vigilance. Une plaque non confirmée est affichée
// comme telle — jamais présentée comme acquise.
const ZONE_LABELS = {
  avant: "Avant", toit: "Toit et vitrages", flanc_gauche: "Flanc gauche",
  flanc_droit: "Flanc droit", arriere: "Arrière",
  jantes_pneus: "Jantes et pneus", interieur: "Intérieur",
};
const ZONE_TONE = { ok: "pos", attention: "warn", probleme: "neg" };
const ZONE_TEXT = { ok: "Rien à signaler", attention: "À surveiller", probleme: "Point dur", non_visible: "Non visible" };

function PhotoAnalysisCard({ data, onAsk, onOpenDossier }) {
  const defauts = data.vigilance_points || data.defauts || data.defects || [];
  const zones = data.zones || {};
  const visibleZones = Object.entries(zones).filter(([, v]) => v && v !== "non_visible");
  const label = [data.make, data.model, data.year].filter(Boolean).join(" ");

  return (
    <Card
      title={label || data.vehicle_guess?.make ? (label || `${data.vehicle_guess.make} ${data.vehicle_guess.model || ""}`.trim()) : "Analyse des photos"}
      sub={data.reused ? "Analyse déjà réalisée, réutilisée" : `${data.photo_count || 0} photo(s)`}>

      {/* Identification issue de la plaque lue sur les photos — le
          professionnel n'a rien saisi. */}
      {data.plate && (
        <div className="viq-rows">
          <Row k="Plaque détectée" v={data.plate}
            tone={data.plate_to_confirm ? "warn" : "pos"} />
          {data.plate_to_confirm && (
            <Row k="Fiabilité de la lecture" v={`${Math.round((data.plate_confidence || 0) * 100)} % — à confirmer`} tone="warn" />
          )}
        </div>
      )}
      {!data.plate && data.vehicle_guess?.make && (
        <div className="viq-card-sub" style={{ marginBottom: ".6rem" }}>
          Aucune plaque lisible sur les photos. Identification probable d'après la carrosserie,
          à confirmer avec une plaque ou un VIN.
        </div>
      )}

      {data.synthese && <div className="viq-answer" style={{ margin: ".7rem 0" }}>{data.synthese}</div>}
      {data.etat_general && !data.synthese && <div className="viq-answer" style={{ margin: ".7rem 0" }}>{data.etat_general}</div>}

      {visibleZones.length > 0 && (
        <div className="viq-rows">
          {visibleZones.map(([k, v]) => (
            <Row key={k} k={ZONE_LABELS[k] || k} v={ZONE_TEXT[v] || v} tone={ZONE_TONE[v]} />
          ))}
        </div>
      )}

      {data.score_delta != null && (
        <div className="viq-rows">
          <Row k="Impact sur le score" v={`${data.score_delta > 0 ? "+" : ""}${data.score_delta} pts`}
            tone={data.score_delta < 0 ? "neg" : "pos"} total />
        </div>
      )}

      {defauts.length > 0 && (
        <>
          <div className="viq-card-sub" style={{ marginTop: ".8rem" }}>Points à contrôler</div>
          <ul className="viq-bullets">
            {defauts.slice(0, 6).map((d, i) => (
              <li key={i}>{typeof d === "string" ? d : (d.titre || d.label)}</li>
            ))}
          </ul>
        </>
      )}

      {data.analysis_unavailable && (
        <div className="viq-card-sub" style={{ marginTop: ".7rem" }}>
          L'analyse visuelle détaillée n'a pas pu aboutir. La plaque lue reste exploitable.
        </div>
      )}
      {data.photos_saved && (
        <div className="viq-chips"><span className="viq-chip accent">Photos enregistrées au dossier</span></div>
      )}

      <Actions items={[
        !data.dossier_id && { label: "Créer le dossier", primary: true, onClick: () => onAsk(data.plate ? `Crée un dossier pour ${data.plate}.` : "Crée un dossier pour ce véhicule.") },
        data.dossier_id && { label: "Ouvrir le dossier", primary: true, onClick: () => onOpenDossier(data.dossier_id) },
        { label: "Chiffrer la remise en état", onClick: () => onAsk("Chiffre la remise en état à partir de ces points.") },
      ]} />
    </Card>
  );
}

// ── Fiabilité ───────────────────────────────────────────────────────────────
function KnownIssuesCard({ data }) {
  if (data.found === false) return <Card title="Fiabilité"><div className="viq-empty">{data.message}</div></Card>;
  return (
    <Card title={data.vehicle || "Fiabilité du modèle"} sub={data.reliability_label}>
      {data.summary && <div className="viq-answer" style={{ marginBottom: ".7rem" }}>{data.summary}</div>}
      {data.vigilance_top5?.length > 0 && (
        <ul className="viq-bullets">
          {data.vigilance_top5.map((v, i) => <li key={i}>{v}</li>)}
        </ul>
      )}
      {data.common_issues?.length > 0 && (
        <div className="viq-rows" style={{ marginTop: ".7rem" }}>
          {data.common_issues.slice(0, 4).map((it, i) => (
            <Row key={i} k={`${it.zone} — ${it.titre}`} v={it.cout_estime || it.km_typique || ""} tone={it.gravite === "majeur" ? "neg" : it.gravite === "modéré" ? "warn" : undefined} />
          ))}
        </div>
      )}
      {data.sources?.length > 0 && (
        <div className="viq-chips">
          {data.sources.slice(0, 3).map((s, i) => (
            <a key={i} className="viq-chip" href={s.url} target="_blank" rel="noopener noreferrer">{s.titre || "source"}</a>
          ))}
        </div>
      )}
    </Card>
  );
}

// ── Marché ──────────────────────────────────────────────────────────────────
function MarketCard({ data }) {
  if (data.found === false) return <Card title="Marché"><div className="viq-empty">{data.message}</div></Card>;
  return (
    <Card title={data.vehicle} sub={`Tension ${data.tension}`}>
      {data.synthese && <div className="viq-answer" style={{ marginBottom: ".6rem" }}>{data.synthese}</div>}
      <div className="viq-rows">
        {data.delai_vente_jours != null && <Row k="Délai de revente observé" v={`${data.delai_vente_jours} jours`} />}
        {data.profil_acheteur && <Row k="Acheteur type" v={data.profil_acheteur} />}
      </div>
      {data.freins_vente?.length > 0 && (
        <ul className="viq-bullets">{data.freins_vente.slice(0, 4).map((f, i) => <li key={i}>{f}</li>)}</ul>
      )}
    </Card>
  );
}

function MarketSearchCard({ data }) {
  const url = data.url || (data.criteria ? buildLeBonCoinUrl(data.criteria) : null);
  const platform = data.platform === "lacentrale" ? "La Centrale" : "LeBonCoin";
  return (
    <Card title={`Vérifier le marché — ${platform}`} sub={data.label}>
      {data.criteria && (
        <div className="viq-chips">
          {data.criteria.brand && <span className="viq-chip">{data.criteria.brand}</span>}
          {data.criteria.keywords && <span className="viq-chip">{data.criteria.keywords}</span>}
          {data.criteria.yearMin && <span className="viq-chip">{data.criteria.yearMin}–{data.criteria.yearMax}</span>}
          {data.criteria.fuel && <span className="viq-chip">{data.criteria.fuel}</span>}
          {data.criteria.kmMax && <span className="viq-chip">≤ {km(data.criteria.kmMax)}</span>}
        </div>
      )}
      {data.note && <div className="viq-card-sub" style={{ marginTop: ".7rem" }}>{data.note}</div>}
      <Actions items={[{ label: `Ouvrir ${platform}`, primary: true, onClick: () => url && window.open(url, "_blank", "noopener,noreferrer") }]} />
    </Card>
  );
}


// ── v222 — Cote VIQ.AI® et fiche 360° ─────────────────────────────────────
function ValuationBar({ low, mid, high, value }) {
  const lo=Number(low), mi=Number(mid), hi=Number(high), val=Number(value || mid);
  if (![lo,mi,hi].every(Number.isFinite) || hi <= lo) return null;
  const pos=Math.max(0,Math.min(100,((val-lo)/(hi-lo))*100));
  const midPos=Math.max(0,Math.min(100,((mi-lo)/(hi-lo))*100));
  return <div style={{margin:".9rem 0 .4rem"}}>
    <div style={{position:"relative",height:10,borderRadius:999,background:"var(--viq-border,#B9ADA1)"}}>
      <div style={{position:"absolute",left:0,right:0,top:3,height:4,borderRadius:999,background:"currentColor",opacity:.18}} />
      <span title="Référence médiane" style={{position:"absolute",left:`${midPos}%`,top:-3,width:2,height:16,background:"currentColor",opacity:.45}} />
      <span title="Cote VIQ.AI®" style={{position:"absolute",left:`calc(${pos}% - 6px)`,top:-1,width:12,height:12,borderRadius:"50%",background:"currentColor",boxShadow:"0 0 0 3px rgba(127,127,127,.18)"}} />
    </div>
    <div style={{display:"flex",justifyContent:"space-between",fontSize:".72rem",opacity:.7,marginTop:5}}><span>{eur(lo)}</span><span>{eur(hi)}</span></div>
  </div>;
}

function ValuationCard({ data, onAsk, onOpenDossier }) {
  if (!data.found) return <Card title="Cote VIQ.AI®"><div className="viq-empty">{data.message || "Données insuffisantes pour calculer la cote."}</div></Card>;
  return <Card title="Cote VIQ.AI®" sub={[data.vehicle, data.km != null ? km(data.km) : null].filter(Boolean).join(" · ")}>
    <div className="viq-rows">
      <Row k="Prix de rachat VIQ.AI®" v={eur(data.cote_viq)} total />
      <Row k="Référence marché basse" v={eur(data.reference_low)} />
      <Row k="Référence médiane" v={eur(data.reference_mid)} />
      <Row k="Référence haute" v={eur(data.reference_high)} />
      <Row k="Confiance" v={data.confidence_label || data.confidence || "Moyenne"} tone={data.confidence === "haute" ? "pos" : data.confidence === "faible" ? "warn" : undefined} />
      {data.market_weight != null && <Row k="Poids du marché" v={`${Math.round(Number(data.market_weight)*100)} %`} />}
    </div>
    <ValuationBar low={data.reference_low} mid={data.reference_mid} high={data.reference_high} value={data.cote_viq} />
    {data.assumptions?.length > 0 && <ul className="viq-bullets">{data.assumptions.map((x,i)=><li key={i}>{x}</li>)}</ul>}
    <div className="viq-card-sub" style={{marginTop:".7rem"}}>Aide à la décision : la valeur finale dépend de l’état réel, de l’historique, du marché local et des frais.</div>
    <Actions items={[
      {label:"Fiche 360°",primary:true,onClick:()=>onAsk("Construis la fiche 360° de ce véhicule.")},
      {label:"Comparer au marché",onClick:()=>onAsk("Compare cette cote au marché actuel.")},
      {label:"Configurer le sourcing",onClick:()=>window.dispatchEvent(new Event("viq-open-opportunity-scan"))},
      data.dossier_id && {label:"Ouvrir le dossier",onClick:()=>onOpenDossier(data.dossier_id)},
    ]}/>
  </Card>;
}

function VehicleBriefCard({ data, onAsk, onOpenDossier }) {
  const v=data.vehicle || {}; const val=data.valuation || null;
  const label=[v.make,v.model,v.version,v.year].filter(Boolean).join(" ") || "Fiche véhicule 360°";
  return <Card title={label} sub={[v.plaque||v.vin, v.km != null ? km(v.km) : null].filter(Boolean).join(" · ") || null}>
    <div className="viq-rows">
      {val?.cote_viq != null && <Row k="Cote VIQ.AI®" v={eur(val.cote_viq)} total />}
      {data.market?.tension && <Row k="Tension marché" v={data.market.tension} />}
      {data.market?.delay_days != null && <Row k="Délai de vente" v={`${data.market.delay_days} j`} />}
      {data.margin?.suggested != null && <Row k="Marge suggérée" v={eur(data.margin.suggested)} />}
      {data.histovec?.owners != null && <Row k="Propriétaires" v={data.histovec.owners} />}
      <Row k="Complétude décisionnelle" v={`${data.completeness || 0} %`} tone={(data.completeness||0)>=70?"pos":"warn"} />
    </div>
    {val && <ValuationBar low={val.reference_low} mid={val.reference_mid} high={val.reference_high} value={val.cote_viq} />}
    <div className="viq-chips">{(data.readiness||[]).map(x=><span key={x.key} className={`viq-chip${x.ok?"":" warn"}`}>{x.ok?"✓ ":"○ "}{x.label}</span>)}</div>
    {data.photo?.points?.length > 0 && <ul className="viq-bullets">{data.photo.points.slice(0,4).map((x,i)=><li key={i}>{typeof x==="string"?x:(x.label||x.titre||x.zone||"Point à vérifier")}</li>)}</ul>}
    <Actions items={[
      !val?.found && {label:"Calculer la cote",primary:true,onClick:()=>onAsk("Donne-moi la Cote VIQ.AI® de ce véhicule.")},
      !data.market && {label:"Analyser le marché",onClick:()=>onAsk("Analyse le marché de ce véhicule.")},
      {label:"Créer un visuel",onClick:()=>onAsk("Génère un visuel commercial premium pour ce véhicule.")},
      data.dossier_id ? {label:"Ouvrir le dossier",onClick:()=>onOpenDossier(data.dossier_id)} : {label:"Créer le dossier",onClick:()=>onAsk("Crée un Dossier VIQ pour ce véhicule.")},
    ]}/>
  </Card>;
}

// ── HistoVec / CarVertical ──────────────────────────────────────────────────
function HistovecCard({ data, onAsk }) {
  if (data.found === false) return <Card title="HistoVec"><div className="viq-empty">{data.message}</div></Card>;
  const flag = (v, bad) => (v === bad ? "neg" : v == null ? undefined : "pos");
  return (
    <Card title="Rapport HistoVec" sub={[data.marque, data.modele].filter(Boolean).join(" ") || null}>
      <div className="viq-rows">
        {data.nb_proprietaires != null && <Row k="Propriétaires" v={data.nb_proprietaires} />}
        {data.ct_valide != null && <Row k="Contrôle technique" v={data.ct_valide ? "En cours de validité" : "À refaire"} tone={flag(data.ct_valide, false)} />}
        {data.sinistres_declares != null && <Row k="Sinistres déclarés" v={data.sinistres_declares} tone={data.sinistres_declares > 0 ? "warn" : "pos"} />}
        {data.gage != null && <Row k="Gage" v={data.gage ? "Véhicule gagé" : "Aucun"} tone={flag(data.gage, true)} />}
        {data.opposition != null && <Row k="Opposition" v={data.opposition ? "Oui" : "Aucune"} tone={flag(data.opposition, true)} />}
        {data.km_dernier_connu != null && <Row k="Dernier kilométrage connu" v={km(data.km_dernier_connu)} />}
      </div>
      {data.resume && <div className="viq-card-sub" style={{ marginTop: ".7rem" }}>{data.resume}</div>}
      <Actions items={[{ label: "Rattacher au dossier", primary: true, onClick: () => onAsk("Rattache ce rapport HistoVec au dossier du véhicule.") }]} />
    </Card>
  );
}

function CarVerticalCard({ data }) {
  return (
    <Card title={data.title} sub={data.vin}>
      <div className="viq-card-sub">{data.note}</div>
      <Actions items={[{ label: "Obtenir le rapport", primary: true, onClick: () => window.open(data.url, "_blank", "noopener,noreferrer") }]} />
    </Card>
  );
}

// ── Annonce ─────────────────────────────────────────────────────────────────
function ListingCard({ data }) {
  const [copied, setCopied] = useState(null);
  const copy = (text, which) => {
    navigator.clipboard?.writeText(text).then(() => { setCopied(which); setTimeout(() => setCopied(null), 1800); });
  };
  return (
    <Card title="Annonce prête à publier" sub={data.recommandation?.plateforme}>
      <div style={{ fontWeight: 650, fontSize: ".92rem", marginBottom: ".5rem" }}>{data.titre}</div>
      <div className="viq-answer" style={{ maxHeight: 220, overflowY: "auto", fontSize: ".86rem" }}>{data.description}</div>
      {data.recommandation?.raison && <div className="viq-card-sub" style={{ marginTop: ".7rem" }}>{data.recommandation.raison}</div>}
      <Actions items={[
        { label: copied === "long" ? "Copié" : "Copier l'annonce", primary: true, onClick: () => copy(`${data.titre}\n\n${data.description}`, "long") },
        { label: copied === "court" ? "Copié" : "Version courte", onClick: () => copy(data.description_courte, "court") },
      ]} />
    </Card>
  );
}

// ── Facture ─────────────────────────────────────────────────────────────────
function InvoiceCard({ data, onAsk, onConfirmSave }) {
  if (data.saved) {
    return <Card title="Charge enregistrée" sub={data.category}>
      <div className="viq-rows">
        <Row k={data.label} v={eur(data.amount_ttc)} tone="pos" />
      </div>
    </Card>;
  }
  if (data.found === false) return <Card title="Facture"><div className="viq-empty">{data.message}</div></Card>;
  const c = (v) => (v == null ? null : v / 100);
  return (
    <Card title={data.supplier || "Facture lue"} sub={data.invoice_number || data.expense_date}>
      <div className="viq-rows">
        <Row k="Libellé" v={data.label} />
        <Row k="Catégorie" v={data.category} />
        {data.amount_ht_cents != null && <Row k="HT" v={eur(c(data.amount_ht_cents))} />}
        {data.vat_cents != null && <Row k="TVA" v={eur(c(data.vat_cents))} />}
        <Row k="TTC" v={eur(c(data.amount_ttc_cents))} total />
      </div>
      {data.vat_on_margin && <div className="viq-chips"><span className="viq-chip warn">TVA sur marge — non récupérable</span></div>}
      {data.warnings?.length > 0 && (
        <ul className="viq-bullets">{data.warnings.slice(0, 3).map((w, i) => <li key={i}>{w}</li>)}</ul>
      )}
      <Actions items={[
        { label: "Enregistrer la charge", primary: true, onClick: () => onConfirmSave && onConfirmSave(data) },
        { label: "Corriger", onClick: () => onAsk("Corrige la facture : ") },
      ]} />
    </Card>
  );
}

// ── Leads / notes / agenda ──────────────────────────────────────────────────
// §27-30, §36-37 — Chaque lead porte ses actions. Un professionnel qui voit
// « VW T-Roc · score 82 » veut appeler ou ouvrir le dossier, pas retaper une
// phrase pour que le modèle le fasse à sa place. Trois actions visibles au
// maximum : au-delà, la liste devient un tableau de boutons illisible.
const LEAD_STATUS = {
  nouveau: ["Nouveau", "warn"], en_cours: ["En cours", undefined],
  converti: ["Converti", "pos"], refuse: ["Refusé", "neg"],
};

function LeadRow({ l, onAction }) {
  const type = String(l.type || "").toLowerCase();
  const typeMeta = type === "achat" ? ["Recherche", "search"] : type === "cartegrise" ? ["Carte grise", "registration"] : ["Estimation", "estimate"];
  const actions = [
    { label: "Afficher", type: "open_lead", id:l.id },
    type === "achat"
      ? { label: "Profil sourcing", type:"open_sourcing_lead", id:l.id }
      : l.dossier_id
        ? { label: "Ouvrir le dossier", type: "open_dossier", id: l.dossier_id }
        : { label: type === "cartegrise" ? "Traiter la carte grise" : "Créer le dossier", type: "prompt", prompt: type === "cartegrise" ? `Traite la demande carte grise du lead #${l.id}.` : `Crée un dossier à partir du lead #${l.id}.` },
    l.phone ? { label: "Appeler", type: "tel", phone: l.phone } : null,
  ].filter(Boolean).slice(0, 3);

  const [st, tone] = LEAD_STATUS[l.status] || [l.status, undefined];
  return (
    <div className="viq-lead-row" data-lead-id={l.id}>
      <div className="viq-lead-main">
        <div className="viq-lead-title">{l.label} <span className={`viq-lead-type ${typeMeta[1]}`}>{typeMeta[0]}</span></div>
        <div className="viq-lead-meta">
          {[l.client, l.score != null ? `Score ${l.score}/100` : null,
            l.km ? `${Number(l.km).toLocaleString("fr-FR")} km` : null,
            l.created_at ? new Date(l.created_at).toLocaleDateString("fr-FR") : null,
           ].filter(Boolean).join(" · ")}
        </div>
      </div>
      <div className="viq-lead-side">
        {st && <span className={`viq-chip${tone === "warn" ? " warn" : tone === "pos" ? " accent" : ""}`}>{st}</span>}
        <div className="viq-lead-actions">
          {actions.map((a, i) => (
            <button key={i} type="button" className="viq-btn" onClick={() => onAction && onAction(a)}>{a.label}</button>
          ))}
        </div>
      </div>
    </div>
  );
}

function LeadList({ data, onAsk, onAction }) {
  // §38-39 — Une carte « aucune demande » ne s'affiche JAMAIS quand une autre
  // carte de la même réponse contient des leads. Deux tools peuvent renvoyer
  // des listes différemment filtrées ; afficher « 9 leads » puis « aucun lead »
  // dans la même réponse détruit la confiance dans tout le reste.
  if (!data.count) {
    if (data.suppressed) return null;
    return <Card title="Leads"><div className="viq-empty">Les nouvelles demandes correspondant à ce filtre apparaîtront ici.</div></Card>;
  }
  return (
    <Card title={`${data.count} ${data.category === "achat" ? "demande" : "lead"}${data.count > 1 ? "s" : ""}${data.category === "achat" ? " de recherche" : data.category === "cartegrise" ? " carte grise" : data.category === "estimation" ? " d’estimation" : ""}`}>
      <div className="viq-lead-list">
        {data.leads.map(l => <LeadRow key={l.id} l={l} onAction={onAction} />)}
      </div>
      <Actions items={[
        { label: "Lesquels traiter en premier ?", onClick: () => onAsk("Lesquels de ces leads dois-je traiter en priorité et pourquoi ?") },
      ]} />
    </Card>
  );
}

function NoteList({ data }) {
  if (!data.count) return <Card title="VIQ.AI Workflow"><div className="viq-empty">{data.due_only ? "Aucune relance à faire." : "Aucune note pour l'instant."}</div></Card>;
  return (
    <Card title={data.due_only ? "Relances à faire" : `${data.count} note${data.count > 1 ? "s" : ""}`}>
      <div className="viq-rows">
        {data.notes.map(n => (
          <Row key={n.id} k={n.title} v={n.follow_up_at ? new Date(n.follow_up_at).toLocaleDateString("fr-FR") : n.action} tone={n.follow_up_at ? "warn" : undefined} />
        ))}
      </div>
    </Card>
  );
}

function AgendaList({ data }) {
  if (!data.count) return <Card title="Agenda"><div className="viq-empty">Rien de prévu sur cette période.</div></Card>;
  return (
    <Card title="Agenda" sub={{ today: "Aujourd'hui", week: "7 jours", month: "30 jours", overdue: "En retard" }[data.range]}>
      <div className="viq-rows">
        {data.items.map(i => (
          <Row key={i.id} k={i.title} v={new Date(i.event_date).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })} tone={i.important ? "warn" : undefined} />
        ))}
      </div>
    </Card>
  );
}

function SimpleSaved({ data, title }) {
  return <Card title={title} sub={data.event_date ? new Date(data.event_date).toLocaleString("fr-FR") : null}>
    <div className="viq-rows"><Row k={data.title || data.label} v="Enregistré" tone="pos" /></div>
  </Card>;
}

function AlertCard({ data }) {
  return (
    <Card title="À prévoir">
      <div className="viq-rows">
        {data.malus_eur != null && <Row k="Malus écologique" v={eur(data.malus_eur)} tone="warn" />}
        {data.carte_grise_eur != null && <Row k="Carte grise" v={eur(data.carte_grise_eur)} />}
        {data.total_eur != null && <Row k="Total" v={eur(data.total_eur)} total />}
      </div>
      {data.note && <div className="viq-card-sub" style={{ marginTop: ".6rem" }}>{data.note}</div>}
    </Card>
  );
}

function DownloadCard({ data }) {
  return (
    <Card title={data.label} sub={data.format.toUpperCase()}>
      <Actions items={[{ label: `Télécharger le ${data.format.toUpperCase()}`, primary: true, onClick: () => window.open(data.url, "_blank", "noopener,noreferrer") }]} />
    </Card>
  );
}

// ── Réseau B2B ──────────────────────────────────────────────────────────────
function NetworkList({ data, onAction, onAsk }) {
  if (!data.count) return <Card title="Réseau"><div className="viq-empty">Aucune opportunité diffusée pour le moment.</div></Card>;
  return (
    <Card title={`${data.count} opportunité${data.count > 1 ? "s" : ""} sur le réseau`}>
      <div className="viq-lead-list">
        {data.opportunities.map(o => (
          <div key={o.publication_id} className="viq-lead-row">
            <div className="viq-lead-main">
              <div className="viq-lead-title">{o.label}</div>
              <div className="viq-lead-meta">
                {[o.km ? `${Number(o.km).toLocaleString("fr-FR")} km` : null, o.fuel,
                  o.asking_price != null ? eur(o.asking_price) : null, o.owner,
                  o.offers_count ? `${o.offers_count} offre${o.offers_count > 1 ? "s" : ""}` : null].filter(Boolean).join(" · ")}
              </div>
            </div>
            <div className="viq-lead-actions">
              <button type="button" className="viq-btn"
                onClick={() => onAction && onAction({ type: "network", id: o.publication_id })}>Voir</button>
              {!o.is_mine && (
                <button type="button" className="viq-btn"
                  onClick={() => onAsk(`Fais une offre sur la publication #${o.publication_id} (${o.label}) : `)}>Faire une offre</button>
              )}
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

function NetworkOfferCard({ data, onAction }) {
  return (
    <Card title="Offre déposée" tone="confirm">
      <div className="viq-rows">
        <Row k="Montant" v={eur(data.amount_eur)} tone="pos" />
        {data.expires_at && <Row k="Valable jusqu'au" v={new Date(data.expires_at).toLocaleString("fr-FR")} />}
        {data.message && <Row k="Message" v={data.message} />}
      </div>
      <div className="viq-card-sub" style={{ marginTop: ".6rem" }}>
        Le propriétaire est notifié. Votre offre reste privée entre vous et lui.
      </div>
      <Actions items={[{ label: "Voir la publication", onClick: () => onAction && onAction({ type: "network", id: data.publication_id }) }]} />
    </Card>
  );
}

// ── Remise en état (§19-24) ─────────────────────────────────────────────────
// Fourchette par poste, total, levier de négociation, confiance et points à
// contrôler physiquement. Compacte par défaut (§92-95) : le détail complet
// s'ouvre à la demande.
const CAT_LABELS = {
  carrosserie: "Carrosserie", peinture: "Peinture", smart_repair: "Smart repair",
  jantes: "Jantes", pneus: "Pneus", vitrage: "Vitrage", optique: "Optiques",
  habitacle: "Habitacle", sellerie: "Sellerie", mecanique: "Mécanique", preparation: "Préparation",
};
const CONF_LABEL = { elevee: "Confiance élevée", moyenne: "Confiance moyenne", basse: "Confiance limitée" };

function ReconditioningCard({ data, onAsk, onAction }) {
  const [full, setFull] = useState(false);
  if (data.found === false) {
    return (
      <Card title="Remise en état">
        <div className="viq-empty">{data.message}</div>
        <Actions items={(data.actions || []).map(a => ({ label: a.label, onClick: () => onAsk(a.prompt) }))} />
      </Card>
    );
  }
  const t = data.total || {};
  const shown = full ? data.items : (data.items || []).slice(0, 4);
  return (
    <Card title="Remise en état" sub={data.vehicle}>
      <div className="viq-rows">
        {shown.map((i, k) => (
          <Row key={k} k={`${CAT_LABELS[i.category] || i.category} — ${i.defect}`}
            v={`${eur(i.low)} – ${eur(i.high)}`}
            tone={i.severity === "important" ? "neg" : i.severity === "leger" ? undefined : "warn"} />
        ))}
        {!full && (data.items || []).length > 4 && (
          <button type="button" className="viq-more" onClick={() => setFull(true)}>
            Voir les {data.items.length - 4} autres postes
          </button>
        )}
        <Row k="Total probable" v={`${eur(t.low)} – ${eur(t.high)}`} total />
        {data.negotiation_lever != null && (
          <Row k="Levier de négociation réaliste" v={eur(data.negotiation_lever)} tone="pos" />
        )}
      </div>

      <div className="viq-chips" style={{ marginTop: ".6rem" }}>
        <span className={`viq-chip${data.confidence === "basse" ? " warn" : ""}`}>{CONF_LABEL[data.confidence] || data.confidence}</span>
        {data.photo_count > 0 && <span className="viq-chip">{data.photo_count} photo(s)</span>}
        {data.segment && <span className="viq-chip">{data.segment}</span>}
      </div>

      {data.to_check_physically?.length > 0 && (
        <>
          <div className="viq-card-sub" style={{ marginTop: ".7rem" }}>À contrôler physiquement</div>
          <ul className="viq-bullets">{data.to_check_physically.slice(0, 4).map((x, i) => <li key={i}>{x}</li>)}</ul>
        </>
      )}
      {full && data.assumptions?.length > 0 && (
        <>
          <div className="viq-card-sub" style={{ marginTop: ".7rem" }}>Hypothèses</div>
          <ul className="viq-bullets">{data.assumptions.map((x, i) => <li key={i}>{x}</li>)}</ul>
          <div className="viq-card-sub" style={{ marginTop: ".5rem" }}>{data.disclaimer}</div>
        </>
      )}

      <Actions items={[
        { label: "Configurer le sourcing", primary: true, onClick: () => window.dispatchEvent(new Event("viq-open-opportunity-scan")) },
        data.dossier_id && { label: "Ouvrir le dossier", onClick: () => onAction && onAction({ type: "open_dossier", dossier_id: data.dossier_id, tab: "margin" }) },
        !full && { label: "Voir l'analyse complète", onClick: () => setFull(true) },
      ]} />
    </Card>
  );
}

// ── §8 (v198) — Représentation compacte d'un onglet dans le chat ────────────
// « Montre-moi la marge » renvoyait une phrase, ou pire un nouveau calcul. La
// donnée existe au dossier : on l'affiche telle quelle, avec le chemin vers
// l'onglet complet. Ces cartes sont servies par le chemin rapide côté serveur,
// donc SANS appel de modèle.
const eurFmt = (v) => (v == null ? null : `${Math.round(Number(v)).toLocaleString("fr-FR")} €`);

function TabSummaryCard({ data, title, rows, headline, onAsk, onAction }) {
  const visible = rows.filter(r => r.value != null && r.value !== "");
  return (
    <Card title={title} sub={data.computed_at
      ? `Enregistré le ${new Date(data.computed_at).toLocaleDateString("fr-FR")}`
      : "Données du Dossier VIQ"}>
      {headline && <div className="viq-headline">{headline}</div>}
      <dl className="viq-kv">
        {visible.map(r => (
          <div className="viq-kv-row" key={r.label}>
            <dt>{r.label}</dt><dd>{r.value}</dd>
          </div>
        ))}
      </dl>
      <Actions items={(data.actions || []).map(a => ({
        label: a.label, primary: a.primary,
        onClick: () => (a.type === "prompt" ? onAsk(a.prompt) : onAction && onAction(a)),
      }))} />
    </Card>
  );
}

function MarginSummaryCard({ data, onAsk, onAction }) {
  return <TabSummaryCard data={data} title="Marge à viser" onAsk={onAsk} onAction={onAction}
    headline={eurFmt(data.marge)}
    rows={[
      { label: "Prix d'achat", value: eurFmt(data.prix_achat) },
      { label: "Frais", value: eurFmt(data.frais) },
      { label: "Prix cible", value: eurFmt(data.prix_revente_cible) },
      { label: "Marge estimée", value: eurFmt(data.marge) },
    ]} />;
}

function MarketSummaryCard({ data, onAsk, onAction }) {
  return <TabSummaryCard data={data} title="Marché" onAsk={onAsk} onAction={onAction}
    headline={eurFmt(data.prix_moyen)}
    rows={[
      { label: "Prix moyen constaté", value: eurFmt(data.prix_moyen) },
      { label: "Tension", value: data.tension || null },
      { label: "Délai de vente", value: data.delai_vente_jours != null ? `${data.delai_vente_jours} jours` : null },
      { label: "Annonces comparées", value: data.nb_annonces ?? null },
      { label: "Synthèse", value: data.synthese || null },
    ]} />;
}

function HistovecSummaryCard({ data, onAsk, onAction }) {
  return <TabSummaryCard data={data} title="HistoVec" onAsk={onAsk} onAction={onAction}
    rows={[
      { label: "Propriétaires", value: data.nb_proprietaires ?? null },
      { label: "Contrôle technique", value: data.ct_valide == null ? null : (data.ct_valide ? "Valide" : "À refaire") },
      { label: "Sinistres déclarés", value: data.sinistres_declares ?? null },
      { label: "Gage", value: data.gage ? "Déclaré" : null },
      { label: "Opposition", value: data.opposition ? "Oui" : null },
    ]} />;
}

// ── §14 — Traduction métier d'un résultat non typé ──────────────────────────
// Le repli d'affichage ne doit jamais montrer la structure interne. Trois
// niveaux, du plus utile au plus neutre :
//   1. un message métier fourni par l'outil (`message`, `note`, `synthese`) ;
//   2. une phrase déduite des champs connus (`count`, `found`, `saved`) ;
//   3. une formule générique — jamais un objet.
function humanizeResult(d = {}, type = null) {
  if (typeof d === "string") return d;
  for (const k of ["message", "note", "synthese", "summary", "label"]) {
    if (typeof d[k] === "string" && d[k].trim()) return d[k].trim();
  }
  if (d.found === false || d.available === false) {
    return "Aucune donnée disponible pour cette demande pour le moment.";
  }
  if (typeof d.count === "number") {
    if (d.count === 0) return "Aucune opportunité correspondant à vos critères n'a été trouvée actuellement.";
    return `${d.count} résultat${d.count > 1 ? "s" : ""} trouvé${d.count > 1 ? "s" : ""}.`;
  }
  if (d.saved === true || d.created === true) return "C'est enregistré.";
  if (d.queued === true) return "Traitement lancé. Je vous préviens dès que c'est prêt.";
  return "C'est fait.";
}

function SourcingCompactPhoto({ src, label }) {
  const [failed, setFailed] = useState(false);
  useEffect(() => { setFailed(false); }, [src]);
  const usable = !!src && !failed;
  return <span className="viq-sourcing-compact-thumb" aria-hidden={!usable}>
    {usable && <img className="viq-sourcing-compact-thumb-img" src={src} alt={`Photo source · ${label}`} loading="lazy" referrerPolicy="no-referrer" onError={() => setFailed(true)} />}
  </span>;
}

function SourcingResultsCard({ data }) {
  const items = Array.isArray(data?.items) ? data.items
    : Array.isArray(data?.results) ? data.results
    : [...(Array.isArray(data?.listings) ? data.listings : []), ...(Array.isArray(data?.search_links) ? data.search_links : [])];
  return <div className="viq-tool-card">
    <div className="viq-tool-card-head"><span>⌕</span><strong>{items.length ? `${items.length} annonce${items.length>1?"s":""} à examiner` : "Sourcing prêt à être élargi"}</strong></div>
    <div className="viq-sourcing-compact-list">{items.slice(0,8).map((x,i)=><a key={x.url||i} href={x.url} target="_blank" rel="noreferrer">
      <SourcingCompactPhoto src={x.image} label={x.title||x.label||"Annonce"} />
      <span className="viq-sourcing-compact-copy"><strong>{x.title||x.label||"Annonce"}</strong><span>{x.source_domain||x.domain||x.source||"Source publique"}</span></span>
    </a>)}</div>
    {!items.length && <p>Élargissez un critère ou relancez la recherche pour explorer davantage d'annonces compatibles.</p>}
  </div>;
}

function OpportunityRadarResultsCard({ data }) {
  const items = Array.isArray(data?.items) ? data.items : [];
  const count = Number(data?.detected ?? data?.count ?? items.length ?? 0);
  return <div className="viq-tool-card viq-radar-chat-summary">
    <div className="viq-tool-card-head"><span>✦</span><strong>VIQ.AI Sourcing multiplateformes terminé</strong></div>
    <p>{count > 0 ? `${count} annonce${count > 1 ? "s" : ""} qualifiée${count > 1 ? "s" : ""} ${count > 1 ? "sont" : "est"} prête${count > 1 ? "s" : ""} à examiner.` : "Le périmètre peut être élargi pour rechercher davantage de candidats compatibles."} Les résultats complets sont disponibles uniquement dans l'onglet VIQ.AI Sourcing.</p>
    <button type="button" className="ws-btn primary sm" onClick={() => window.dispatchEvent(new Event("viq-show-opportunities"))}>Afficher les résultats</button>
  </div>;
}

function WebResearchResultsCard({ data, onAsk }) {
  const sources = Array.isArray(data?.sources) ? data.sources : [];
  const direct = Array.isArray(data?.direct_searches) ? data.direct_searches : [];
  return (
    <Card title="Recherche web métier" sub={data?.query || null}>
      {data?.note && <div className="viq-card-text">{data.note}</div>}
      {data?.synthesis && <div className="viq-answer" style={{marginTop:".65rem"}}>{data.synthesis}</div>}
      {sources.length > 0 && <div className="viq-web-sources" style={{display:"grid",gap:".5rem",marginTop:".65rem"}}>
        {sources.map((s,i)=><div key={s.url||i} className="viq-web-source" style={{border:"1px solid var(--ws-border,var(--border))",borderRadius:10,padding:".62rem .7rem",background:"var(--ws-surface,var(--card))"}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:".7rem"}}>
            <div style={{minWidth:0}}><strong style={{fontSize:".79rem",lineHeight:1.35}}>{s.title||s.source_domain||`Source ${i+1}`}</strong><div className="viq-card-sub" style={{marginTop:".16rem"}}>{[s.source_domain,s.published_at?new Date(s.published_at).toLocaleDateString("fr-FR"):null].filter(Boolean).join(" · ")}</div></div>
            <button type="button" className="viq-btn" onClick={()=>window.open(s.url,"_blank","noopener,noreferrer")}>Ouvrir</button>
          </div>
          {s.description&&<div className="viq-card-sub" style={{marginTop:".35rem",lineHeight:1.45}}>{s.description}</div>}
        </div>)}
      </div>}
      {direct.length > 0 && <div className="viq-actions" style={{marginTop:".6rem",flexWrap:"wrap"}}>{direct.map((d,i)=><button type="button" className="viq-btn" key={d.url||i} onClick={()=>window.open(d.url,"_blank","noopener,noreferrer")}>Recherche {d.label}</button>)}</div>}
      {onAsk && <div className="viq-actions" style={{marginTop:".55rem"}}><button type="button" className="viq-btn primary" onClick={()=>onAsk("Recoupe ces sources, compare les informations et donne-moi les implications concrètes pour mon activité VO.")}>Recouper et conseiller</button></div>}
    </Card>
  );
}

function ListingUrlCard({ data, onAsk }) {
  return (
    <Card title={data?.found ? "Annonce analysée" : "Lecture de l’annonce"} sub={data?.platform || null}>
      {data?.title && <div className="viq-card-text"><strong>{data.title}</strong></div>}
      {data?.description && <div className="viq-card-sub" style={{ marginTop: ".35rem" }}>{data.description}</div>}
      <div className="viq-card-text" style={{ marginTop: ".55rem" }}>{data?.message || "Analyse terminée."}</div>
      <Actions items={[
        data?.url && { label:"Ouvrir l’annonce", onClick:()=>window.open(data.url,"_blank","noopener,noreferrer") },
        data?.found && { label:"Évaluer l’opportunité", primary:true, onClick:()=>onAsk("Évalue cette annonce comme opportunité d'achat : marché, coûts, risques et marge potentielle.") },
      ].filter(Boolean)} />
    </Card>
  );
}

function GenericResultCard({ data, type, onAsk, onAction }) {
  if (typeof console !== "undefined" && console.debug) console.debug("[viq-card] type non typé :", type, data);
  const text = humanizeResult(data, type);
  const actions = Array.isArray(data?.actions) ? data.actions : [];
  const mapped = actions.map(a => ({
    label: a.label, primary: a.primary,
    onClick: () => {
      if (a.type === "link" && a.url) { window.open(a.url, "_blank", "noopener,noreferrer"); return; }
      return (a.type === "prompt" || !a.type ? onAsk(a.prompt || a.label) : onAction && onAction(a));
    },
  }));
  const trivial = /^(c['’]est fait\.?|fait\.?|terminé\.?|action effectuée\.?)$/i.test(String(text || "").trim());
  if (trivial && !mapped.length) return null;
  return <div className="viq-generic-result">{!trivial && text && <div className="viq-card-text">{text}</div>}{mapped.length > 0 && <Actions items={mapped} />}</div>;
}

// ── Résultat média (§4, §13-15, §34) ────────────────────────────────────────
// La version précédente n'offrait AUCUN téléchargement : le professionnel
// voyait son showroom sans pouvoir le récupérer. Et ses boutons envoyaient des
// phrases — `onAsk("Crée une vidéo … avec ces photos.")` — que le modèle devait
// réinterpréter à chaque fois, avec le risque de ne pas retrouver « ces
// photos ». Les actions transportent désormais des `media_ids` (§14-15).
const MEDIA_TITLES = {
  remove_background: "Arrière-plan supprimé", showroom: "Showroom généré",
  enhance: "Photos améliorées", blur_plate: "Plaque floutée",
  studio: "Fond studio", listing: "Photos prêtes pour l'annonce", video: "Vidéo générée",
};

function MediaResultCard({ data, onAsk, onAction }) {
  const [idx, setIdx] = useState(0);
  const [showBefore, setShowBefore] = useState(false);
  const [busy, setBusy] = useState(null);
  const [notice, setNotice] = useState(null);

  if (data.found === false) {
    return (
      <Card title="Traitement des photos">
        <div className="viq-empty">{data.message}</div>
        <Actions items={(data.actions || []).map(a => ({
          label: a.label, primary: a.primary,
          onClick: () => (a.type === "prompt" ? onAsk(a.prompt) : onAction && onAction(a)),
        }))} />
      </Card>
    );
  }

  const results = data.results || [];
  const cur = results[Math.min(idx, results.length - 1)];
  const isVideo = cur?.kind === "video";
  const mediaIds = data.media_ids || results.map(r => r.media_id).filter(Boolean);
  const vehicle = data.vehicle || {};

  const run = async (key, fn, okMessage) => {
    setBusy(key); setNotice(null);
    try { await fn(); if (okMessage) setNotice(okMessage); }
    catch (e) { setNotice(e.message || "Action impossible."); }
    finally { setBusy(null); setTimeout(() => setNotice(null), 4000); }
  };

  return (
    <Card title={data.title || MEDIA_TITLES[data.mode] || "Photos traitées"} sub={data.note}>
      {isVideo ? (
        <video className="viq-media" src={cur.url} controls playsInline preload="metadata" />
      ) : cur ? (
        <>
          <img className="viq-media" src={showBefore ? cur.before : cur.after}
            alt={showBefore ? "Photo avant traitement" : "Photo après traitement"} />
          <div className="viq-media-bar">
            <button type="button" className={`viq-btn${showBefore ? " primary" : ""}`} onClick={() => setShowBefore(true)}>Avant</button>
            <button type="button" className={`viq-btn${showBefore ? "" : " primary"}`} onClick={() => setShowBefore(false)}>Après</button>
            {results.length > 1 && (
              <span className="viq-media-nav">
                <button type="button" className="viq-btn" disabled={idx === 0} onClick={() => setIdx(i => i - 1)} aria-label="Photo précédente">‹</button>
                <span>{idx + 1} / {results.length}</span>
                <button type="button" className="viq-btn" disabled={idx >= results.length - 1} onClick={() => setIdx(i => i + 1)} aria-label="Photo suivante">›</button>
              </span>
            )}
          </div>
        </>
      ) : null}

      {/* §34 — Échec partiel : on dit ce qui reste utilisable, sans répéter
          l'erreur à chaque photo ratée. */}
      {data.errors?.length > 0 && (
        <div className="viq-card-sub" style={{ marginTop: ".5rem" }}>
          {data.errors.length === 1
            ? "Une des photos n'a pas pu être transformée. Les autres résultats restent disponibles."
            : `${data.errors.length} photos n'ont pas pu être transformées. Les autres résultats restent disponibles.`}
        </div>
      )}
      {notice && <div className="viq-card-sub" style={{ marginTop: ".5rem" }}>{notice}</div>}

      <Actions items={[
        // §4 — Téléchargement, enfin.
        { label: busy === "dl" ? "Téléchargement…" : (results.length > 1 ? "Télécharger cette photo" : "Télécharger"),
          primary: true,
          onClick: () => run("dl", () => downloadResult(cur, { vehicle, mode: data.mode, index: idx })) },
        results.length > 1 && {
          label: busy === "zip" ? "Préparation de l'archive…" : "Télécharger toutes les photos",
          onClick: () => run("zip", () => downloadMediaZip(mediaIds, { vehicle, mode: data.mode })) },
        data.dossier_id && { label: "Enregistrer dans le dossier",
          onClick: () => onAction && onAction({ type: "open_dossier", dossier_id: data.dossier_id, tab: "photos" }) },
        // §15 — L'annonce reçoit les identifiants, pas « ces photos ».
        { label: "Utiliser dans l'annonce",
          onClick: () => onAction && onAction({ type: "generate_listing", media_ids: mediaIds, dossier_id: data.dossier_id || null }) },
        // §14 — Idem pour la vidéo : plus de texte réinterprété par le modèle.
        !isVideo && { label: "Créer une vidéo",
          onClick: () => onAction && onAction({ type: "generate_video", media_ids: mediaIds, dossier_id: data.dossier_id || null }) },
        { label: "Refaire",
          onClick: () => onAction && onAction({ type: "retry_media", media_ids: mediaIds, mode: data.mode }) },
      ]} />
    </Card>
  );
}

// ── Génération vidéo asynchrone (§35-37) ────────────────────────────────────
// La vidéo demande deux minutes. La carte affiche donc un ÉTAT, interrogé par
// sondage, et la conversation reste utilisable pendant ce temps — c'est tout
// l'objet du passage en job.
function MediaJobCard({ data, onAsk, onAction }) {
  const [job, setJob] = useState(null);
  const jobId = data.job_id;

  useEffect(() => {
    if (!jobId) return undefined;
    let alive = true;
    let delay = 4000;
    let timer = null;
    const tick = async () => {
      const r = await apiGet(`/api/viqai/media/jobs/${jobId}`).catch(() => null);
      if (!alive) return;
      if (r?.job) {
        setJob(r.job);
        if (r.job.status === "completed" || r.job.status === "failed") return;
      }
      // Sondage dégressif : rapide au début, plus espacé ensuite. Une requête
      // toutes les quatre secondes pendant deux minutes serait du gaspillage.
      delay = Math.min(delay * 1.25, 12000);
      timer = setTimeout(tick, delay);
    };
    timer = setTimeout(tick, 3000);
    return () => { alive = false; if (timer) clearTimeout(timer); };
  }, [jobId]);

  const status = job?.status || data.status || "processing";
  const result = job?.result || null;

  if (status === "completed" && result?.media) {
    return (
      <Card title={data.title || "Vidéo prête"}
        sub={result.duration ? `${result.duration} s · ${result.format}${result.branded ? " · branding professionnel" : ""}` : result.format}>
        <video className="viq-media" src={`${result.media.download_url}?inline=1`} controls playsInline preload="metadata" />
        {/* §7 — La durée affichée est la durée MESURÉE après assemblage, jamais
            la durée demandée. Si l'assemblage n'a pas pu se faire, on le dit. */}
        {result.ffmpeg === false && (
          <div className="viq-card-sub" style={{ marginTop: ".5rem" }}>
            Assemblage indisponible sur ce serveur : la vidéo livrée correspond à la première séquence.
          </div>
        )}
        <Actions items={[
          { label: "Télécharger la vidéo", primary: true,
            onClick: () => downloadResult({ media_id: result.media.id, filename: result.media.filename }) },
          data.dossier_id && { label: "Enregistrer dans le dossier",
            onClick: () => onAction && onAction({ type: "open_dossier", dossier_id: data.dossier_id, tab: "photos" }) },
          { label: "Créer une annonce",
            onClick: () => onAction && onAction({ type: "generate_listing", media_ids: [result.media.id], dossier_id: data.dossier_id || null }) },
          { label: "Regénérer", onClick: () => onAction && onAction({ type: "generate_video", media_ids: [] }) },
        ]} />
      </Card>
    );
  }

  if (status === "failed") {
    return (
      <Card title="La vidéo n'a pas pu être générée" tone="warn">
        <div className="viq-empty">{job?.error || "La génération a échoué."}</div>
        <Actions items={[
          { label: "Réessayer", primary: true, onClick: () => onAction && onAction({ type: "generate_video", media_ids: [] }) },
          { label: "Changer de format", onClick: () => onAsk("Crée plutôt une version horizontale de la vidéo.") },
        ]} />
      </Card>
    );
  }

  return (
    <Card title={data.title || "Création de la vidéo…"} sub={data.message}>
      <div className="viq-progress" role="status" aria-live="polite">
        <span className="viq-spinner" aria-hidden="true">✦</span>
        {job?.progress || "Création de la vidéo…"}
      </div>
      <div className="viq-card-sub" style={{ marginTop: ".4rem" }}>
        Vous pouvez continuer à travailler : je vous préviens dès qu'elle est prête.
      </div>
      <Actions items={(data.actions || []).map(a => ({
        label: a.label, onClick: () => onAction && onAction(a),
      }))} />
    </Card>
  );
}

// ── Médiathèque (§43-44) ────────────────────────────────────────────────────
function MediaLibraryCard({ data, onAction }) {
  const [tab, setTab] = useState("edited");
  if (!data.found) {
    return <Card title="Médiathèque"><div className="viq-empty">Aucun visuel produit pour ce véhicule pour l'instant.</div></Card>;
  }
  const groups = {
    edited: (data.images || []).filter(m => m.mode !== "original"),
    original: (data.images || []).filter(m => m.mode === "original"),
    video: data.videos || [],
  };
  const list = groups[tab] || [];
  return (
    <Card title="Médiathèque" sub={`${data.count} média(s)${data.dossier_id ? ` · dossier #${data.dossier_id}` : ""}`}>
      <div className="viq-media-tabs" role="tablist">
        {[["edited", "Retouchées"], ["original", "Originales"], ["video", "Vidéos"]].map(([k, label]) => (
          <button key={k} type="button" role="tab" aria-selected={tab === k}
            className={`viq-btn${tab === k ? " primary" : ""}`} onClick={() => setTab(k)}>
            {label} {groups[k].length > 0 && `(${groups[k].length})`}
          </button>
        ))}
      </div>
      <div className="viq-media-grid">
        {list.map(m => (
          <figure key={m.id} className="viq-media-thumb">
            {m.type === "video"
              ? <video src={`${m.download_url}?inline=1`} controls preload="metadata" />
              : <img src={`${m.download_url}?inline=1`} alt={m.filename} loading="lazy" />}
            <figcaption>
              <button type="button" className="viq-btn" onClick={() => downloadResult({ media_id: m.id, filename: m.filename })}>Télécharger</button>
            </figcaption>
          </figure>
        ))}
        {!list.length && <div className="viq-empty">Rien dans cette catégorie.</div>}
      </div>
      <Actions items={[
        list.length > 1 && { label: "Télécharger tout", primary: true,
          onClick: () => downloadMediaZip(list.map(m => m.id), { mode: tab }) },
        data.dossier_id && { label: "Ouvrir le dossier",
          onClick: () => onAction && onAction({ type: "open_dossier", dossier_id: data.dossier_id, tab: "photos" }) },
      ]} />
    </Card>
  );
}

// ── Workflow (§49) ──────────────────────────────────────────────────────────
function WorkflowTaskCard({ data, onAction }) {
  const t = data.task || {};
  return (
    <Card title="Tâche créée" tone="confirm">
      <div className="viq-rows">
        <Row k={t.title} v={t.due_at ? new Date(t.due_at).toLocaleString("fr-FR") : "Sans échéance"} />
        {data.in_agenda && <Row k="Ajoutée à l'agenda" v="Oui" tone="pos" />}
      </div>
      <Actions items={[
        { label: "Ouvrir le workflow", primary: true, onClick: () => onAction && onAction({ type: "navigate", destination: "notes" }) },
        t.dossier_id && { label: "Ouvrir le dossier", onClick: () => onAction && onAction({ type: "open_dossier", dossier_id: t.dossier_id }) },
      ]} />
    </Card>
  );
}

function WorkflowListCard({ data, onAction, onAsk }) {
  if (!data.count) return <Card title="Workflow"><div className="viq-empty">Aucune tâche en cours.</div></Card>;
  return (
    <Card title={`${data.count} tâche${data.count > 1 ? "s" : ""} en cours`}
      sub={[data.overdue ? `${data.overdue} en retard` : null, data.due_today ? `${data.due_today} aujourd'hui` : null].filter(Boolean).join(" · ") || null}>
      <div className="viq-rows">
        {data.tasks.map(t => (
          <Row key={t.id} k={t.title}
            v={t.due_at ? new Date(t.due_at).toLocaleDateString("fr-FR") : "—"}
            tone={t.due_at && new Date(t.due_at) < new Date() ? "neg" : undefined} />
        ))}
      </div>
      <Actions items={[{ label: "Ouvrir le workflow", primary: true, onClick: () => onAction && onAction({ type: "navigate", destination: "notes" }) }]} />
    </Card>
  );
}

// ── Quota épuisé — l'incitation au forfait ──────────────────────────────────
// Volontairement sobre : pas d'alarme rouge, pas de « erreur ». Le
// professionnel vient de voir ce que l'agent sait faire ; le message se
// contente de lui dire comment continuer.
function QuotaCard({ data, onUpgrade }) {
  return (
    <Card title="Vos analyses offertes sont épuisées" tone="confirm">
      <div className="viq-answer" style={{ fontSize: ".9rem" }}>{data.message}</div>
      <ul className="viq-bullets">
        <li>Analyses de véhicules et de photos sans limite</li>
        <li>Intelligence marché et analyse documentaire</li>
        <li>Mémoire complète de vos dossiers</li>
      </ul>
      <Actions items={[
        { label: "Découvrir les forfaits VIQ.AI", primary: true, onClick: () => onUpgrade && onUpgrade() },
      ]} />
    </Card>
  );
}

// ── Confirmation d'action sensible (§14) ────────────────────────────────────
function ActionConfirmation({ card, onConfirm, onCancel, resolved }) {
  if (resolved) {
    return <Card title="Action confirmée" tone="confirm">
      <div className="viq-rows"><Row k={card.data.label} v="Exécutée" tone="pos" /></div>
    </Card>;
  }
  return (
    <Card title="VIQ.AI souhaite" tone="confirm">
      <div className="viq-answer" style={{ fontSize: ".9rem" }}>{card.data.label}</div>
      <Actions items={[
        { label: "Confirmer", primary: true, onClick: () => onConfirm(card) },
        { label: "Annuler", onClick: () => onCancel(card) },
      ]} />
    </Card>
  );
}

// ── Prochaines actions (§34, §35) ───────────────────────────────────────────
// Construites à partir de ce que la carte contient RÉELLEMENT, pas d'une liste
// fixe : proposer « importer HistoVec » alors qu'il vient de l'être fait
// apprend à l'utilisateur à ignorer ces boutons.
const NEXT_ACTIONS = {
  valuation_card: () => [
    { label: "Fiche 360°", ask: "Construis la fiche 360° de ce véhicule." },
    { label: "Comparer au marché", ask: "Compare cette cote au marché actuel." },
  ],
  photo_analysis_card: (d) => [
    { label: "Chiffrer les travaux", ask: "Chiffre les travaux à prévoir à partir de ces défauts." },
    !d.dossier_id && { label: "Identifier le véhicule", ask: "Identifie ce véhicule, voici sa plaque : " },
    !d.dossier_id && { label: "Créer le dossier", ask: "Crée un dossier pour ce véhicule." },
  ],
  vehicle_card: () => [
    { label: "Analyser la fiabilité", ask: "Quels sont les points faibles connus de ce modèle ?" },
    { label: "Voir le marché", ask: "Cherche les annonces comparables de ce véhicule." },
  ],
  histovec_card: (d) => [
    { label: "Évaluer le risque", ask: "Quel risque représente ce véhicule au vu de son historique ?" },
    { label: "Préparer CarVertical", ask: "Prépare le rapport CarVertical de ce véhicule." },
    d.dossier_id && { label: "Calculer la marge", ask: "Calcule ma marge sur ce véhicule." },
  ],
  margin_card: (d) => [
    d.dossier_id && { label: "Comparer au marché", ask: "Compare ce véhicule aux annonces du marché." },
    { label: "Générer une annonce", ask: "Génère une annonce de vente pour ce véhicule." },
  ],
  known_issues_card: () => [
    { label: "Chiffrer la négociation", ask: "Chiffre la négociation à partir de ces points de vigilance." },
  ],
};

function NextActions({ card, onAsk }) {
  const build = NEXT_ACTIONS[card?.type];
  if (!build) return null;
  const items = (build(card.data || {}) || []).filter(Boolean).slice(0, 4);
  if (!items.length) return null;
  return (
    <div className="viq-next">
      {items.map((a, i) => (
        <button key={i} type="button" className="viq-suggestion" onClick={() => onAsk(a.ask)}>{a.label}</button>
      ))}
    </div>
  );
}

export { NextActions };

// ── Aiguillage ──────────────────────────────────────────────────────────────
export default function ViqCard({ card, onAsk, onAction, onOpenDossier, onOpenInterface, onConfirm, onCancel, onUpgrade, resolved }) {
  const d = card?.data || {};
  switch (card?.type) {
    case "valuation_card":       return <ValuationCard data={d} onAsk={onAsk} onOpenDossier={onOpenDossier} />;
    case "vehicle_brief_card":   return <VehicleBriefCard data={d} onAsk={onAsk} onOpenDossier={onOpenDossier} />;
    case "opportunity_radar_results": return <OpportunityRadarResultsCard data={d} onAsk={onAsk} />;
    case "sourcing_results":     return <SourcingResultsCard data={d} onAsk={onAsk} />;
    case "web_research_results": return <WebResearchResultsCard data={d} onAsk={onAsk} />;
    case "listing_url_card":     return <ListingUrlCard data={d} onAsk={onAsk} />;
    case "vehicle_card":         return <VehicleCard data={d} onAsk={onAsk} onOpenDossier={onOpenDossier} />;
    case "vehicle_list":         return <VehicleListCard data={d} onAsk={onAsk} onOpenDossier={onOpenDossier} />;
    case "dossier_card":         return <DossierCard data={d} onAsk={onAsk} onOpenDossier={onOpenDossier} />;
    case "dossier_list":         return <DossierList data={d} onOpenDossier={onOpenDossier} onAsk={onAsk} />;
    case "margin_card":          return <MarginCard data={d} onAsk={onAsk} onOpenDossier={onOpenDossier} />;
    case "photo_analysis_card":  return <PhotoAnalysisCard data={d} onAsk={onAsk} onOpenDossier={onOpenDossier} />;
    case "known_issues_card":    return <KnownIssuesCard data={d} />;
    case "market_card":          return <MarketCard data={d} />;
    case "market_search_card":   return <MarketSearchCard data={d} />;
    case "histovec_card":        return <HistovecCard data={d} onAsk={onAsk} />;
    case "carvertical_card":     return <CarVerticalCard data={d} />;
    case "listing_card":         return <ListingCard data={d} />;
    case "invoice_card":         return <InvoiceCard data={d} onAsk={onAsk} onConfirmSave={() => onAsk("Enregistre cette charge.")} />;
    case "lead_list":            return <LeadList data={d} onAsk={onAsk} onAction={onAction} />;
    case "note_list":            return <NoteList data={d} />;
    case "note_card":            return <SimpleSaved data={d} title="Note ajoutée" />;
    case "email_card":           return <SimpleSaved data={d} title="E-mail envoyé" />;
    case "agenda_list":          return <AgendaList data={d} />;
    case "agenda_card":          return <SimpleSaved data={d} title="Ajouté à l'agenda" />;
    case "alert_card":           return <AlertCard data={d} />;
    case "download_card":        return <DownloadCard data={d} />;
    case "open_interface":
      // La modale existante s'ouvre, pré-remplie : on ne la remplace pas (§12).
      onOpenInterface && onOpenInterface(d);
      return null;
    case "network_list":         return <NetworkList data={d} onAction={onAction} onAsk={onAsk} />;
    case "network_offer_card":   return <NetworkOfferCard data={d} onAction={onAction} />;
    case "reconditioning_card":  return <ReconditioningCard data={d} onAsk={onAsk} onAction={onAction} />;
    case "media_result":         return <MediaResultCard data={d} onAsk={onAsk} onAction={onAction} />;
    case "media_job":            return <MediaJobCard data={d} onAsk={onAsk} onAction={onAction} />;
    case "margin_summary":       return <MarginSummaryCard data={d} onAsk={onAsk} onAction={onAction} />;
    case "market_summary":       return <MarketSummaryCard data={d} onAsk={onAsk} onAction={onAction} />;
    case "histovec_summary":     return <HistovecSummaryCard data={d} onAsk={onAsk} onAction={onAction} />;
    case "media_library":        return <MediaLibraryCard data={d} onAction={onAction} />;
    case "workflow_task_card":   return <WorkflowTaskCard data={d} onAction={onAction} />;
    case "workflow_list":        return <WorkflowListCard data={d} onAction={onAction} onAsk={onAsk} />;
    case "quota_card":           return <QuotaCard data={d} onUpgrade={onUpgrade} />;
    case "action_confirmation":  return <ActionConfirmation card={card} onConfirm={onConfirm} onCancel={onCancel} resolved={resolved} />;
    default:
      // §14 — Un type de carte inconnu ne doit JAMAIS déverser son JSON à
      // l'écran. C'est exactement ce que faisait ce repli :
      // `{JSON.stringify(d).slice(0, 300)}` affichait
      // { "note":"…", "count":0, "found":true, "errors":[] } au professionnel.
      // On le traduit en langage métier ; le JSON part en console pour le
      // diagnostic, là où il a sa place.
      return <GenericResultCard data={d} type={card?.type} onAsk={onAsk} onAction={onAction} />;
  }
}
